import * as React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import QualificationAnimation from './QualificationAnimation';
import SectionHeader from './SectionHeader';
import AvailabilityAnimation from './AvailabilityAnimation';
import IntegrationsAnimation from './IntegrationsAnimation';
import OutboundAnimation from './OutboundAnimation';

const features = [
  {
    title: '24/7 call answering',
    description: 'Callers get a real conversation at 2am, on Sundays, and on holidays, on whatever schedule you set. Nobody gets sent to voicemail.',
  },
  {
    title: 'Qualification and lead scoring',
    description: 'Your intake questions, asked your way. Each lead is scored against your criteria, and qualified callers are booked straight onto your calendar.',
  },
  {
    title: 'Outbound calls and texts',
    description: "Web leads get a call back in under 15 seconds, and two-way texting runs from your firm's own verified numbers.",
  },
  {
    title: 'Works with the tools you have',
    description: 'Clio, Filevine, Litify, Salesforce, your phone system, and your calendar. Leads and booked consults land where your team already works.',
  },
];

export default function Features() {
  const [selectedIndex, setSelectedIndex] = React.useState(0);

  return (
    <Box
      id="features"
      sx={{
        pt: { xs: 2, sm: 2.5, md: 3.5 },
        pb: { xs: 6, sm: 8, md: 10 },
        backgroundColor: 'background.default',
      }}
    >
      <Container maxWidth="lg">
        <SectionHeader
          title="From the first ring to a booked consult"
          subtitle="OpenIntake runs the whole intake conversation by phone, text, chat, or form, then puts the consultation on your calendar."
        />

        {/* Mobile Carousel Layout */}
        <Box
          sx={{
            display: { xs: 'flex', md: 'none' },
            flexDirection: 'column',
            gap: 3,
            minWidth: 0,
          }}
        >
          {/* Animation Card */}
          <Card
            sx={{
              width: '100%',
              minWidth: 0,
              height: 380,
              borderRadius: 4,
              backgroundColor: '#FFFFFF',
              border: '1px solid #E8E2D8',
              boxShadow: 'none',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 0,
              position: 'relative',
              touchAction: 'pan-y', // Allow vertical scrolling to pass through
              pointerEvents: 'none', // Don't capture pointer events
            }}
          >
            {/* Animated content wrapper */}
            {features.map((_, index) => (
              <Box
                key={index}
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: selectedIndex === index ? 1 : 0,
                  transform: selectedIndex === index ? 'scale(1)' : 'scale(0.95)',
                  transition: 'opacity 0.4s ease, transform 0.4s ease',
                  pointerEvents: 'none',
                }}
              >
                {index === 0 && <AvailabilityAnimation key="availability-mobile" />}
                {index === 1 && <QualificationAnimation key="qualification-mobile" />}
                {index === 2 && <OutboundAnimation key="outbound-mobile" />}
                {index === 3 && <IntegrationsAnimation key="integrations-mobile" />}
              </Box>
            ))}
          </Card>

          {/* Navigation Controls */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, minHeight: 100 }}>
            {/* Left Arrow */}
            <IconButton
              onClick={() => setSelectedIndex(prev => prev === 0 ? features.length - 1 : prev - 1)}
              sx={{
                backgroundColor: '#F7F3EB',
                color: '#6B5F4D',
                '&:hover': { backgroundColor: '#E8E2D8' },
                transition: 'all 0.2s ease',
                mt: 0.5,
              }}
            >
              <ChevronLeftIcon />
            </IconButton>

            {/* Current Feature Info */}
            <Box sx={{ flex: 1, textAlign: 'center', minWidth: 0, overflow: 'hidden', position: 'relative', minHeight: 90 }}>
              <Box
                key={selectedIndex}
                sx={{
                  animation: 'fadeSlideIn 0.4s ease',
                  '@keyframes fadeSlideIn': {
                    '0%': { opacity: 0, transform: 'translateY(8px)' },
                    '100%': { opacity: 1, transform: 'translateY(0)' },
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    color: '#3E301E',
                    wordBreak: 'break-word',
                    mb: 1,
                  }}
                >
                  {features[selectedIndex].title}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    color: '#6B5F4D',
                    lineHeight: 1.5,
                  }}
                >
                  {features[selectedIndex].description}
                </Typography>
              </Box>
            </Box>

            {/* Right Arrow */}
            <IconButton
              onClick={() => setSelectedIndex(prev => prev === features.length - 1 ? 0 : prev + 1)}
              sx={{
                backgroundColor: '#F7F3EB',
                color: '#6B5F4D',
                '&:hover': { backgroundColor: '#E8E2D8' },
                mt: 0.5,
              }}
            >
              <ChevronRightIcon />
            </IconButton>
          </Box>

          {/* Dots Indicator */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
            {features.map((_, index) => (
              <Box
                key={index}
                onClick={() => setSelectedIndex(index)}
                sx={{
                  width: selectedIndex === index ? 24 : 8,
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: selectedIndex === index ? '#B5683C' : '#E8E2D8',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                }}
              />
            ))}
          </Box>
        </Box>

        {/* Desktop Side-by-Side Layout */}
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'row',
            gap: 6,
            alignItems: 'flex-start',
            minWidth: 0,
          }}
        >
          {/* Left: Feature Cards */}
          <Box
            sx={{
              flex: '0 0 45%',
              display: 'flex',
              flexDirection: 'column',
              minWidth: 0,
            }}
          >
            {features.map((feature, index) => {
              const selected = selectedIndex === index;
              return (
                <Box
                  key={feature.title}
                  component="button"
                  type="button"
                  onClick={() => setSelectedIndex(index)}
                  aria-pressed={selected}
                  sx={{
                    all: 'unset',
                    cursor: 'pointer',
                    display: 'block',
                    py: 2.5,
                    pl: 3,
                    pr: 2,
                    borderLeft: '2px solid',
                    borderColor: selected ? '#B5683C' : '#E8E2D8',
                    transition: 'border-color 0.2s ease',
                    '&:hover': { borderColor: selected ? '#B5683C' : '#C4B99D' },
                    '&:focus-visible': { outline: '2px solid #B5683C', outlineOffset: 2 },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '1.125rem',
                      fontWeight: 600,
                      letterSpacing: '-0.01em',
                      color: selected ? '#3E301E' : '#6B5F4D',
                      transition: 'color 0.2s ease',
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateRows: selected ? '1fr' : '0fr',
                      transition: 'grid-template-rows 0.3s ease',
                    }}
                  >
                    <Typography
                      sx={{
                        overflow: 'hidden',
                        color: '#6B5F4D',
                        lineHeight: 1.6,
                        fontSize: '0.975rem',
                        pt: selected ? 1 : 0,
                        transition: 'padding 0.3s ease',
                      }}
                    >
                      {feature.description}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>

          {/* Right: Product Mockup */}
          <Box
            sx={{
              flex: '0 0 55%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minWidth: 0,
            }}
          >
            <Card
              sx={{
                width: '100%',
                maxWidth: 500,
                minWidth: 0,
                height: 450,
                borderRadius: 4,
                backgroundColor: '#FFFFFF',
                border: '1px solid #E8E2D8',
                boxShadow: 'none',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 0,
              }}
            >
              {/* Feature-specific animations */}
              {selectedIndex === 0 && <AvailabilityAnimation key="availability" />}
              {selectedIndex === 1 && <QualificationAnimation key="qualification" />}
              {selectedIndex === 2 && <OutboundAnimation key="outbound" />}
              {selectedIndex === 3 && <IntegrationsAnimation key="integrations" />}
            </Card>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
